import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';
import { Box, Snackbar, Alert, AlertColor, AlertTitle, Button as MuiButton } from '@mui/material';

/*
 * Toast Notification System
 * =========================
 * Built on MUI Snackbar + Alert for visual consistency with the rest of the app.
 *
 * Architecture:
 * - ToastProvider wraps the app and holds toast state
 * - useToast() hook gives any component access to toast functions
 * - Uses MUI Snackbar for positioning/animation and Alert for styling
 *
 * API:
 *   const { success, error, warning, info, dismiss, dismissAll } = useToast();
 *   success('Saved!');
 *   error('Something failed', { title: 'Update Error', duration: 6000 });
 *   success('Deleted', { action: { label: 'Undo', onClick: handleUndo } });
 *
 * Design decisions:
 *   - Bottom-right: doesn't cover sidebar (left) or header (top)
 *   - 4s auto-dismiss: long enough to read, short enough to not clutter
 *   - Max 5 visible: prevents UI flooding from rapid-fire actions
 *   - MUI Alert: consistent with the app's Material Design language
 *   - Stacked toasts: each toast is its own Snackbar, stacked vertically
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  title?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastOptions {
  type?: ToastType;
  /** Duration in ms. Set to 0 for persistent (manual dismiss only). Default: 4000 */
  duration?: number;
  /** Optional title displayed above the message */
  title?: string;
  /** Optional action button (e.g., "Undo") */
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  toast: (message: string, options?: ToastOptions) => string;
  success: (message: string, options?: Omit<ToastOptions, 'type'>) => string;
  error: (message: string, options?: Omit<ToastOptions, 'type'>) => string;
  warning: (message: string, options?: Omit<ToastOptions, 'type'>) => string;
  info: (message: string, options?: Omit<ToastOptions, 'type'>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 4000;

let counter = 0;

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast() must be used within <ToastProvider>. ' +
      'Wrap your app root in <ToastProvider>.'
    );
  }
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => setToasts([]), []);

  const addToast = useCallback((message: string, options: ToastOptions = {}): string => {
    const id = `toast-${++counter}-${Date.now()}`;
    const item: ToastItem = {
      id,
      message,
      type: options.type || 'info',
      duration: options.duration ?? DEFAULT_DURATION,
      title: options.title,
      action: options.action,
    };

    setToasts((prev) => {
      const next = [...prev, item];
      return next.length > MAX_VISIBLE ? next.slice(-MAX_VISIBLE) : next;
    });

    return id;
  }, []);

  const value: ToastContextValue = {
    toast: useCallback((msg, opts) => addToast(msg, opts), [addToast]),
    success: useCallback((msg, opts) => addToast(msg, { ...opts, type: 'success' }), [addToast]),
    error: useCallback((msg, opts) => addToast(msg, { ...opts, type: 'error' }), [addToast]),
    warning: useCallback((msg, opts) => addToast(msg, { ...opts, type: 'warning' }), [addToast]),
    info: useCallback((msg, opts) => addToast(msg, { ...opts, type: 'info' }), [addToast]),
    dismiss,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Box sx={{
        position: 'fixed',
        right: { xs: 16, sm: 24 },
        bottom: { xs: 16, sm: 24 },
        width: { xs: 'calc(100% - 32px)', sm: 420 },
        maxWidth: 420,
        maxHeight: 'calc(100vh - 48px)',
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 1.5,
        overflowY: 'auto',
        pointerEvents: 'none',
        zIndex: (theme) => theme.zIndex.snackbar + 10,
      }}>
      {toasts.map((t) => (
        <Snackbar
          key={t.id}
          open={true}
          autoHideDuration={t.duration > 0 ? t.duration : null}
          onClose={(_event, reason) => {
            // Don't dismiss on clickaway — only on timeout or explicit close
            if (reason === 'clickaway') return;
            dismiss(t.id);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            // Let the stack account for each message's actual height.
            position: 'relative',
            left: { xs: 'auto', sm: 'auto' },
            right: { xs: 'auto', sm: 'auto' },
            top: { xs: 'auto', sm: 'auto' },
            bottom: { xs: 'auto', sm: 'auto' },
            width: '100%',
            flexShrink: 0,
            pointerEvents: 'auto',
          }}
        >
          <Alert
            severity={t.type as AlertColor}
            variant="filled"
            onClose={() => dismiss(t.id)}
            sx={{
              width: '100%',
              minWidth: 0,
              maxWidth: 420,
              boxShadow: 'var(--shadow-lg)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-family)',
              fontSize: 'var(--font-size-small)',
              fontWeight: 'var(--font-weight-medium)',
              alignItems: 'center',
              '& .MuiAlert-message': {
                flex: 1,
                minWidth: 0,
                overflowWrap: 'anywhere',
              },
              '& .MuiAlert-action': {
                pt: 0,
              },
            }}
            action={
              t.action ? (
                <MuiButton
                  color="inherit"
                  size="small"
                  onClick={() => {
                    t.action!.onClick();
                    dismiss(t.id);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'var(--font-weight-semibold)',
                    fontSize: 'var(--font-size-xs)',
                    ml: 1,
                  }}
                >
                  {t.action.label}
                </MuiButton>
              ) : undefined
            }
          >
            {t.title && <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>{t.title}</AlertTitle>}
            {t.message}
          </Alert>
        </Snackbar>
      ))}
      </Box>
    </ToastContext.Provider>
  );
};
